import React, { useState, useEffect } from "react";
import { Box, Button, Card, TextField, Typography, Grid, MenuItem, Select, InputLabel, FormControl, Dialog } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const PaymentScreen = ({ payload, proceed, closePopup, editData, editMode }) => {

    const [dropdownSubjectData, setDropdownSubjectData] = useState([]);
    const [attachmentData, setAttachmentData] = useState([]); // array now
    const [previewImage, setPreviewImage] = useState(null);

    // 🔥 NEW: tracks which existing transaction (DB transactionId) is being edited.
    // Empty string = user is adding a brand-new payment/installment.
    const [selectedTxnId, setSelectedTxnId] = useState("");

    const [paymentDetails, setPaymentDetails] = useState({
        totalAmount: payload?.amountToPay || 0,        // from previous screen
        paidAmount: 0,        // user enters
        mode: "",
        alreadyPaid: 0,   // ✅ old paid
        enterAmount: 0,
        date: "",
        transactionId: 0,     // NOTE: this is the transactionReference field (UPI/txn ref no.), NOT the DB transactionId
        remarks: "",
        attachment: null,
        remark: "",
        ddNumber: 0
    });
    const [deletedAttachments, setDeletedAttachments] = useState([]);

    const handleRemoveAttachment = (index) => {
        const isSuccess = window.confirm("Are you sure you want to delete this image?");

        if (!isSuccess) return;

        setAttachmentData((prev) => {
            const removed = prev[index];

            // ✅ If it's OLD file → track for deletion
            if (!removed.file) {
                setDeletedAttachments((d) => [...d, removed]);
            }

            return prev.filter((_, i) => i !== index);
        });
    };
    const isDDMode = Number(paymentDetails.mode) === 1;

    // ✅ On editData load: only set the read-only summary fields (total/already paid/admin remark).
    // Do NOT auto-prefill a specific transaction's fields anymore — user must pick one from the list below,
    // otherwise we can't reliably know which of the N transactions they intend to edit.
    useEffect(() => {
        if (editData) {

            const total = Number(editData?.totalAmount || 0);

            const totalPaid = (editData?.transactions || [])
                .reduce((sum, txn) => sum + Number(txn?.paidAmount || 0), 0);

            setPaymentDetails((prev) => ({
                ...prev,
                totalAmount: total,
                alreadyPaid: totalPaid,
                remarks: editData?.adminRemark || "",
            }));

            // reset selection state whenever editData changes
            setSelectedTxnId("");
            setAttachmentData([]);
        }
    }, [editData]);

    // 🔥 NEW: called when user clicks a transaction row in the "select transaction to edit" list
    const handleSelectTransaction = (txn) => {
        const isDD = Number(txn?.paymentMode) === 1;

        setSelectedTxnId(txn.transactionId);

        setPaymentDetails((prev) => ({
            ...prev,
            enterAmount: Number(txn.paidAmount || 0),
            mode: txn.paymentMode || "",
            date: txn.transactionDate
                ? new Date(txn.transactionDate).toLocaleDateString("en-CA")
                : "",
            transactionId: isDD ? "" : (txn.transactionReference || ""),
            ddNumber: isDD ? (txn.transactionReference || "") : "",
            remark: txn.transaction_remark || "",
        }));

        try {
            const names = JSON.parse(txn?.attachmentName || "[]");
            const paths = JSON.parse(txn?.attachmentPath || "[]");

            const mapped = paths.map((p, i) => ({
                file: null,   // existing file, no re-upload unless user changes it
                name: names[i] || p.originalName || `file-${i}`,
                path: p.path
            }));

            setAttachmentData(mapped);
        } catch (err) {
            console.log("Attachment parse error", err);
        }
    };

    // 🔥 NEW: user wants to add a fresh installment instead of editing an existing one
    const handleAddNewPaymentInstead = () => {
        setSelectedTxnId("");
        setPaymentDetails((prev) => ({
            ...prev,
            enterAmount: 0,
            mode: "",
            date: "",
            transactionId: "",
            ddNumber: "",
            remark: ""
        }));
        setAttachmentData([]);
    };

    const [subjectMap, setSubjectMap] = useState({});
    console.log(editData, "editDataeditDataeditData")

    useEffect(() => {
        const loadData = async () => {
            let results = await window.Platform.database.getOlympiadDropdownData();

            if (results?.status) {
                setDropdownSubjectData(results.data.subjects);

                // ✅ create lookup map
                const map = {};
                results.data.subjects.forEach((sub) => {
                    map[sub.subject_name.trim().toLowerCase()] = sub.id;
                });

                setSubjectMap(map);
            }
        };

        loadData();
    }, []);

    useEffect(() => {
        setPaymentDetails((prev) => ({
            ...prev,
            totalAmount: payload?.amountToPay || 0   // 👈 make sure you pass this
        }));
    }, [payload]);

    const handleChange = (field, value) => {
        setPaymentDetails((prev) => {
            let updated = { ...prev, [field]: value };

            // 🔥 RESET LOGIC ON MODE CHANGE
            if (field === "mode") {
                updated.transactionId = "";
                updated.ddNumber = "";
            }

            return updated;
        });
    };

    const handleSubmit = async () => {

        if (!paymentDetails.mode) {
            alert("Select payment mode");
            return;
        }

        if (!paymentDetails.date) {
            alert("Select payment date");
            return;
        }

        const enterAmount1 = Number(paymentDetails.enterAmount || 0);

        // ✅ ONLY validate amount if something is payable
        if (!editMode && remainingBefore > 0) {
            if (!enterAmount1 || enterAmount1 <= 0) {
                alert("Enter valid amount");
                return;
            }
        }

        if (remainingBefore > 0) {
            if (isDDMode && !paymentDetails.ddNumber) {
                alert("Enter DD Number");
                return;
            }

            if (!isDDMode && !paymentDetails.transactionId) {
                alert("Enter Transaction ID");
                return;
            }
        }

        if (paymentDetails.remark == "") {
            alert("Enter Transaction Remark")
            return;
        }
        if (paymentDetails.remark?.length < 20) {
            alert("Transaction Remark Must be 20 characters")
            return;
        }

        if (paymentDetails.remarks == "") {
            alert("Enter Admin Remark")
            return
        }
        if (paymentDetails.remarks?.length < 20) {
            alert("Admin Remark Must be 20 characters")
            return
        }

        if (!attachmentData.length) {
            const confirmNoAttachment = window.confirm(
                "No attachment uploaded. Are you sure you want to proceed without attachment?"
            );

            if (!confirmNoAttachment) return;
        }

        const enterAmount = Number(paymentDetails.enterAmount || 0);

        const transactionData = [
            {
                // 🔥 NEW: when editing an existing transaction this carries its DB transactionId,
                // so the backend UPDATEs that row instead of INSERTing a new one.
                // Left undefined when adding a brand-new installment.
                transactionId: editMode && selectedTxnId ? selectedTxnId : undefined,
                paidAmount: Number(paymentDetails.enterAmount),
                paymentMode: paymentDetails.mode,
                transactionReference: isDDMode ? paymentDetails.ddNumber : paymentDetails.transactionId,
                transactionDate: paymentDetails.date,
                attachmentName: paymentDetails.attachment?.name || "",
                attachmentPath: "", // 🔥 set after upload API if needed
                remark: paymentDetails?.remark
            }
        ];
        const cordinators = payload?.coordinators || [];

        console.log("💰subjectMapsubjectMap", subjectMap, ">>>>>>>>>>>>>>>>>>>>>", cordinators);
        console.log(payload, "payloadpayload")
        const mappedCoordinators = cordinators.map((c) => ({
            name: c?.name,
            email: c?.email,
            mobile: c?.mobile,
            role: c?.role || c?.designation,
            subject: c?.subject || ""
        }));

        const finalPayload = buildFinalPayload(payload, transactionData, mappedCoordinators);

        console.log(finalPayload, "finalPayload");

        let response;
        try {

            if (editMode) {
                response = await window.Platform.database.updateOlympiadRegistration({
                    ...finalPayload,
                    regId: editData?.regId
                });
            } else {
                response = await window.Platform.database.createOlympiadRegistration(finalPayload);
            }

            if (response?.status) {
                const regId = response?.regId;
                const transactionId = response?.transaction_id;

                try {
                    for (let i = 0; i < attachmentData.length; i++) {
                        if (!attachmentData[i].file) continue; // ✅ skip old files
                        const formData = new FormData();
                        formData.append("files", attachmentData[i].file);
                        formData.append("regId", regId);
                        formData.append("transactionId", transactionId);

                        await window.Platform.database.uploadTransactionAttachment(formData);
                    }

                    window.NotificationUtils.showSuccess(response?.message || "Successfully Done")
                    closePopup();

                } catch (err) {
                    // window.NotificationUtils.showError("Attachment upload failed");
                }
            }
            else {
                window.NotificationUtils.showError("Something went wrong");
            }
        } catch (error) {
            console.error("API Error:", error);
            window.NotificationUtils.showError("Submission failed. Please try again.");
        }
    };

    function buildFinalPayload(payload, paymentData = [], coordinators = []) {
        return {
            registration_type: payload?.olympiadDetails?.registrationTypeId,
            olympiadTypeId: payload?.olympiadDetails?.olympiadTypeId,

            userId: payload?.ownerId || editData?.userId,

            partyId: payload?.partyId || editData?.partyId,

            datesOfOlympiad: payload?.olympiadDetails?.olympiadDateId,

            medium: payload?.olympiadDetails?.mediumId,

            adminRemark: paymentDetails.remarks || "",

            totalAmount: Number(payload?.amountToPay || 0),

            lineItems: (payload?.olympiadDetails?.classSubjects || [])
                .flatMap((cls) =>
                    (cls.subjects || [])
                        .filter((sub) =>
                            Number(sub.studentCount || 0) > 0 ||
                            Number(sub.materialQty || 0) > 0
                        )
                        .map((sub) => ({
                            mediumId: payload?.olympiadDetails?.mediumId,
                            classId: cls.classId,
                            subjectId: subjectMap[sub.subjectName?.trim()?.toLowerCase()] || "",
                            student: Number(sub.studentCount || 0),
                            study_material: Number(sub.materialQty || 0),
                            mittlernPresent: true
                        }))
                ),

            // ✅ PAYMENT ARRAY
            transactionData: paymentData,

            // ✅ COORDINATORS
            coordinators: coordinators || []
        };
    }


    const total = Number(paymentDetails.totalAmount || 0);
    const alreadyPaid = Number(paymentDetails.alreadyPaid || 0);
    const enterAmount = Number(paymentDetails.enterAmount || 0);

    const remainingBefore = total - alreadyPaid;

    const finalBalance = total - (alreadyPaid + enterAmount);

    return (
        <Box p={2}>

            <Typography variant="h5" mb={2}>
                Payment Details
            </Typography>

            {/* 🔥 NEW: Transaction picker — only shown in edit mode when there are existing transactions */}
            {editMode && editData?.transactions?.length > 0 && (
                <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" mb={1}>Select Transaction to Edit</Typography>

                    {editData.transactions.map((txn) => (
                        <Box
                            key={txn.transactionId}
                            onClick={() => handleSelectTransaction(txn)}
                            sx={{
                                p: 1.5, mb: 1, borderRadius: 1, cursor: "pointer",
                                border: selectedTxnId === txn.transactionId ? "2px solid #1976d2" : "1px solid #ccc",
                                backgroundColor: selectedTxnId === txn.transactionId ? "#e3f2fd" : "#fff"
                            }}
                        >
                            <Typography fontWeight={600}>
                                ₹{txn.paidAmount} — Ref: {txn.transactionReference} — {txn.transactionStatus}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {new Date(txn.transactionDate).toLocaleDateString("en-GB")} • {txn.transaction_remark}
                            </Typography>
                        </Box>
                    ))}

                    <Button
                        size="small"
                        variant={selectedTxnId ? "outlined" : "contained"}
                        sx={{ mt: 1 }}
                        onClick={handleAddNewPaymentInstead}
                    >
                        + Add New Payment Instead
                    </Button>
                </Card>
            )}

            <Card sx={{ p: 2 }}>
                <Grid container spacing={2}>

                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel>Payment Mode</InputLabel>
                            <Select
                                label="Payment Mode"
                                value={paymentDetails.mode}
                                onChange={(e) => handleChange("mode", e.target.value)}
                            >
                                {payload?.paymentModes?.map((mode) => (
                                    <MenuItem key={mode.id} value={mode.id}>
                                        {mode.payment_mode}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* 📅 PAYMENT DATE */}
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Payment Date"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={paymentDetails.date}
                            onChange={(e) =>
                                handleChange("date", e.target.value)
                            }
                        />
                    </Grid>

                    {/* 💰 TOTAL AMOUNT (READ ONLY) */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Total Amount"
                            value={`₹ ${paymentDetails.totalAmount}`}
                            InputProps={{ readOnly: true }}
                        />
                    </Grid>

                    {/* Already Paid */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Amount Paid"
                            value={`₹ ${(paymentDetails.alreadyPaid) || 0}`}
                            InputProps={{ readOnly: true }}
                        />
                    </Grid>

                    {/* Remaining */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Amount to Pay"
                            value={`₹ ${remainingBefore}`}
                            InputProps={{ readOnly: true }}
                        />
                    </Grid>

                    {/* Enter New Amount */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Enter Amount"
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            value={paymentDetails.enterAmount}
                            onChange={(e) => {
                                let value = Number(e.target.value);
                                handleChange("enterAmount", value);
                            }}
                        />
                        {enterAmount > 0 && (
                            <>
                                {finalBalance > 0 && (
                                    <Typography color="error" fontWeight="bold">
                                        Due Amount is ₹ {finalBalance}
                                    </Typography>
                                )}

                                {finalBalance < 0 && (
                                    <Typography
                                        sx={{ color: "green", fontWeight: "bold" }}
                                    >
                                        You paid extra ₹ {Math.abs(finalBalance)}
                                    </Typography>
                                )}

                                {finalBalance === 0 && (
                                    <Typography
                                        sx={{ color: "green", fontWeight: "bold" }}
                                    >
                                        Payment Completed
                                    </Typography>
                                )}
                            </>
                        )}
                    </Grid>

                    {/* ✅ DD MODE → DD NUMBER */}
                    <Grid item xs={12}>
                        {isDDMode ? (
                            <TextField
                                fullWidth
                                label="DD Number"
                                value={paymentDetails.ddNumber}
                                onChange={(e) => handleChange("ddNumber", e.target.value)}
                            />
                        ) : (
                            <TextField
                                fullWidth
                                label="Transaction ID"
                                value={paymentDetails.transactionId}
                                onChange={(e) => handleChange("transactionId", e.target.value)}
                            />
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Remark"
                            value={paymentDetails.remark}
                            onChange={(e) =>
                                handleChange("remark", e.target.value)
                            }
                        />
                    </Grid>

                    {/* 📝 REMARKS */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Remarks"
                            multiline
                            rows={3}
                            value={paymentDetails.remarks}
                            onChange={(e) =>
                                handleChange("remarks", e.target.value)
                            }
                        />
                    </Grid>

                    {/* 📎 ATTACHMENT */}
                    <Grid item xs={12}>
                        <Button variant="outlined" component="label">
                            Upload Attachment
                            <input
                                type="file"
                                multiple
                                hidden
                                onChange={(e) => {
                                    const files = Array.from(e.target.files);

                                    const mappedFiles = files.map((file) => ({
                                        file: file,
                                        name: file.name,
                                        path: `${window.Platform.database.funcForBaseURL()}/file/${file.name}`
                                    }));

                                    setAttachmentData((prev) => [...prev, ...mappedFiles]);
                                }}
                            />
                        </Button>

                        {attachmentData.length > 0 && (
                            <Box mt={1}>
                                <Grid container spacing={2} mt={1}>
                                    {attachmentData.map((att, i) => {
                                        const imageUrl = att.file
                                            ? URL.createObjectURL(att.file) // new upload preview
                                            : `${window.Platform.database.funcForBaseURL()}/file/${att.name}`;

                                        return (
                                            <Grid item xs={3} key={i}>
                                                <Box textAlign="center">
                                                    <DeleteIcon
                                                        onClick={() => handleRemoveAttachment(i)}
                                                        style={{
                                                            position: "absolute",
                                                            cursor: "pointer",
                                                            color: "#d90000",
                                                            background: "#fff",
                                                            borderRadius: "50%"
                                                        }}
                                                    />
                                                    <img
                                                        src={imageUrl}
                                                        alt={att.name}
                                                        onClick={() => setPreviewImage(imageUrl)}
                                                        style={{
                                                            width: "100%",
                                                            height: "120px",
                                                            objectFit: "cover",
                                                            borderRadius: "8px",
                                                            cursor: "pointer",
                                                            border: "1px solid #ccc"
                                                        }}
                                                    />
                                                    <Typography variant="caption">
                                                        {att.name}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        )}
                    </Grid>

                </Grid>
            </Card>

            {/* 🚀 SUBMIT */}
            <Box mt={3} display="flex" justifyContent="flex-end">
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                >
                    Submit Payment
                </Button>
            </Box>
            <Dialog
                open={!!previewImage}
                onClose={() => setPreviewImage(null)}
                maxWidth="md"
            >
                {previewImage && (
                    <img
                        src={previewImage}
                        alt="preview"
                        style={{
                            width: "100%",
                            height: "auto"
                        }}
                    />
                )}
            </Dialog>
        </Box>
    );
};

export default PaymentScreen;